import { useEffect, useState } from 'react';
import { Auth, Hub } from 'aws-amplify';

/**
 * Custom React hook to integrate AWS Amplify authentication.
 *
 * @returns {Object} An object containing:
 *   - authStatus {string}: Represents the current authentication state. Can be 'authenticated', 'unauthenticated', or 'configuring'.
 *   - authUser {Object}: The current authenticated user object. Empty object if unauthenticated.
 *   - getAuthUser {Function}: Function to manually trigger user authentication state check and retrieval.
 *
 * @example
 * const { authStatus, authUser, getAuthUser } = useAmplifyAuth();
 */
const useAmplifyAuth = () => {
    const [authUser, setAuthUser] = useState({});
    const [authStatus, setAuthStatus] = useState('configuring');

    const getAuthUser = async () => {
        setAuthStatus('configuring');
        try {
            const user = await Auth.currentAuthenticatedUser();
            setAuthUser(user);
            setAuthStatus('authenticated');
            // Remove the code and state from the URL so they don't leak
            const params = new URLSearchParams(window.location.search);
            if (params.has('code') || params.has('state')) {
                const newUrl = window.location.pathname;
                window.history.replaceState({}, document.title, newUrl);
            }
        } catch (error) {
            setAuthUser({});
            setAuthStatus('unauthenticated');
        }
    };

    useEffect(() => {
        getAuthUser();
    }, []);

    useEffect(() => {
        const listener = (data) => {
            const { payload } = data;
            const { event } = payload;
            if (event === 'signIn') {
                const user = payload.data;
                setAuthUser(user);
                setAuthStatus('authenticated');
            } else if (event === 'signIn_failure') {
                setAuthUser({});
                setAuthStatus('unauthenticated');
            } else if (event === 'signOut') {
                setAuthUser({});
                setAuthStatus('unauthenticated');
            }
        };

        const stopListening = Hub.listen('auth', listener);
        return () => stopListening();
    }, []);

    return { authStatus, authUser, getAuthUser };
};

export default useAmplifyAuth;
